import React, { useState, useRef } from 'react';
import { formatNumber, formatMoney, isValidNumber } from '../../utils/numberFormatting';
import BigNumber from 'bignumber.js';
import { Wallet } from 'lucide-react';
import { MIN_VALUES } from '../../constants/minValues';
import NumericSpinnerButtons from './NumericSpinnerButtons';

const Parameter = ({ label, value, icon: Icon, onChange, onOpenSettings, isDirectEditable = false, prefix, suffix, children, staticDisplay }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState('');
  const [width, setWidth] = useState(null);
  const paramRef = useRef(null);

  // Determine appropriate step size based on decimal precision of value
  const getAppropriateStep = (strValue) => {
    // Default step for integer values
    if (!strValue || !strValue.includes('.')) return 1;

    // Count decimal places to determine precision
    const decimalPart = strValue.split('.')[1];
    const precision = decimalPart.length;

    // Return step based on precision (0.1 for 1 decimal place, 0.01 for 2 decimal places, etc.)
    return new BigNumber(10).pow(-precision);
  };

  const getNumericValue = (val) => {
    if (typeof val === 'object' && val !== null && 'value' in val) {
      return val.value.toString();
    }
    if (typeof val === 'string') {
      if (val.startsWith('$')) {
        return val.slice(1);
      }
      if (val.endsWith('%') || val.endsWith('x')) {
        return val.slice(0, -1);
      }
      if (val.endsWith(' p')) {
        return val.slice(0, -2);
      }
    }
    return val.toString();
  };

  const formatValue = (value) => {
    if (typeof value === 'object' && value !== null) {
      const { value: val, unit } = value;
      if (unit === 'percent') return `${formatNumber(val, 2)}%`;
      return formatMoney(val, true);
    }

    if (typeof value === 'string') {
      if (value.startsWith('$')) {
        if (label === 'Risk') {
          // Special handling for Risk parameter
          const number = new BigNumber(value.slice(1));
          // For large numbers (≥1000) always use compact formatting
          if (number.isGreaterThanOrEqualTo(1000)) {
            return formatMoney(number, true); // Compact formatting (e.g. $9k)
          }
          // For small integers, remove decimal zeros
          const isInteger = number.isInteger();
          if (isInteger) {
            return `$${number.toFixed(0)}`;
          }
          // For fractional numbers, use normal formatting
          return formatMoney(number, false);
        }
        return formatMoney(value.slice(1), true);
      }
      if (value.endsWith('%') || value.endsWith('x')) {
        return value;
      }
    }

    if (typeof value === 'number' || value?.constructor?.name === 'BigNumber') {
      return formatNumber(value, 2);
    }

    return value;
  };

  // Maps label to state field update
  const applyChange = (numValue) => {
    if (typeof onChange !== 'function') return;
    if (label === 'R:R') {
      onChange(prev => ({ ...prev, nominalRR: numValue }));
    } else if (label === 'Risk') {
      onChange(prev => ({ ...prev, risk: numValue }));
    } else if (label === 'Fee') {
      onChange(prev => ({ ...prev, entryFee: numValue, exitFee: numValue }));
    } else if (label === 'Sprd') {
      onChange(prev => ({ ...prev, spread: numValue }));
    } else if (Icon === Wallet) {
      onChange(prev => ({ ...prev, accountBalance: numValue }));
    } else {
      onChange(prev => ({ ...prev, maxLeverage: numValue }));
    }
  };

  // Check minimum value for a given label; returns true if below min
  const isBelowMin = (numValue) => {
    if (label === 'R:R') return numValue.isLessThan(MIN_VALUES.nominalRR);
    if (label === 'Risk') return numValue.isLessThan(MIN_VALUES.risk);
    if (label === 'Sprd') return numValue.isLessThan(0);
    if (Icon === Wallet) return numValue.isLessThan(MIN_VALUES.accountBalance);
    if (!Icon && !label) return numValue.isLessThan(MIN_VALUES.maxLeverage);
    return false;
  };

  // Get the clamped minimum value for a given label
  const getMinValue = () => {
    if (label === 'R:R') return MIN_VALUES.nominalRR;
    if (label === 'Risk') return MIN_VALUES.risk;
    if (label === 'Sprd') return new BigNumber(0);
    if (Icon === Wallet) return MIN_VALUES.accountBalance;
    return MIN_VALUES.maxLeverage;
  };

  const handleClick = () => {
    if (!isEditing) {
      // Multi-TP: R:R is no longer a single editable value — show ×N and route the
      // click to Settings where the exits are configured.
      if (staticDisplay) {
        if (onOpenSettings) onOpenSettings(null);
        return;
      }
      if (isDirectEditable && paramRef.current) {
        const elementWidth = paramRef.current.offsetWidth;
        setWidth(elementWidth);
        setIsEditing(true);
        const numericValue = getNumericValue(value);
        setLocalValue(numericValue);
      } else if (onOpenSettings) {
        const field = label === 'Fees' ? 'fees' : Icon === Wallet ? 'accountBalance' : null;
        onOpenSettings(field);
      }
    }
  };

  const isNumericOnly = label === 'R:R' || label === 'Risk' || label === 'Sprd' || (!Icon && !label);

  const handleChange = (e) => {
    const newValue = e.target.value;
    if (isNumericOnly) {
      if (!/^\d*\.?\d*$/.test(newValue)) {
        return;
      }
    }

    setLocalValue(newValue);
    if (newValue && isValidNumber(newValue)) {
      const numValue = new BigNumber(newValue);
      if (isBelowMin(numValue)) return;
      applyChange(numValue);
    }
  };

  const handleBlur = () => {
    if (localValue && isValidNumber(localValue)) {
      const numValue = new BigNumber(localValue);
      const originalValue = new BigNumber(getNumericValue(value));

      // Skip onChange if value hasn't changed — prevents unnecessary recalculation and UI flicker
      if (!numValue.isEqualTo(originalValue)) {
        applyChange(numValue);
      }
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (isNumericOnly && e.key === '-') {
      e.preventDefault();
      return;
    }

    if (e.key === 'Escape') {
      setIsEditing(false);
      setLocalValue(getNumericValue(value));
      return;
    }

    if (e.key === 'Enter') {
      if (localValue && isValidNumber(localValue)) {
        const numValue = new BigNumber(localValue);
        applyChange(numValue);
      }
      e.target.blur();
      return;
    }

    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      try {
        const currentValue = new BigNumber(localValue || '0');
        const baseStep = getAppropriateStep(localValue);
        const step = e.shiftKey ? baseStep.times(10) : baseStep;
        const newValue = e.key === 'ArrowUp' ? currentValue.plus(step) : currentValue.minus(step);

        if (isBelowMin(newValue)) {
          const minVal = getMinValue();
          setLocalValue(minVal.toString());
          applyChange(minVal);
          return;
        }

        setLocalValue(newValue.toString());
        applyChange(newValue);
      } catch (error) {
        console.error('Error handling arrow keys:', error);
      }
    }
  };

  const handleSpinnerChange = (direction) => {
    const currentValue = new BigNumber(localValue || '0');
    const step = getAppropriateStep(localValue);
    const newValue = direction === 'up' ? currentValue.plus(step) : currentValue.minus(step);

    if (isBelowMin(newValue)) {
      const minVal = getMinValue();
      setLocalValue(minVal.toString());
      applyChange(minVal);
      return;
    }

    setLocalValue(newValue.toString());
    applyChange(newValue);
  };

  const baseClasses = "h-10 px-4 bg-slate-800 rounded-lg";

  if (isEditing) {
    return (
      <div
        className={`${baseClasses} border border-amber-500/20 focus-within:border-amber-500/50 relative`}
        style={{ width: width ? `${width}px` : 'auto' }}
      >
        <input
          inputMode="decimal"
          pattern="[0-9]*[.]?[0-9]*"
          value={localValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          step="any"
          min={isNumericOnly ? "0" : undefined}
          autoFocus
          className="w-full h-full bg-transparent text-slate-200 text-sm outline-none pr-8"
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          <NumericSpinnerButtons
            onIncrement={() => handleSpinnerChange('up')}
            onDecrement={() => handleSpinnerChange('down')}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      ref={paramRef}
      className={`${baseClasses} flex items-center hover:bg-slate-800/60 cursor-pointer border border-transparent`}
      onClick={handleClick}
    >
      {Icon ? (
        <Icon size={16} className="text-slate-500 mr-2 flex-shrink-0" />
      ) : label ? (
        <span className="text-slate-500 text-sm mr-2">{label}</span>
      ) : null}
      {children}
      <span className="text-slate-300 text-sm ml-auto whitespace-nowrap">{staticDisplay || formatValue(value)}</span>
    </div>
  );
};

export default Parameter;
